import { useMemo } from 'react';

import { Card, Form } from '@arco-design/web-react';

import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { isSkuCapableProfile } from '../../../platform/pipeline-kernel';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { APP_INSTANCE_CONFIG_PREVIEW_EXCLUDED_MODULES } from '../display/productCard';
import { APP_INSTANCE_MODULES } from '../modules';
import { APP_INSTANCE_DIRECT_MERGED_MODULES } from '../scenes';

const PREVIEW_WATCH_FIELDS = [
  'alias',
  'workspace',
  'description',
  'runtime',
  '_inferenceSystem',
  'inferenceFrameworkImage',
  'maasInferenceSource',
  'runMode',
  'masterNodes',
  'workerNodes',
  'commonEnv',
  'environment',
  'environment.env',
  '_appStorageGroups',
  'dataMountPath',
  '_inferenceService',
  '_serviceMountGroupName',
  '_containerDeployHealthCheck',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '-';
/** 预览模块顺序与直建创建一致 */
const ALL_MODULE_KEYS = [...APP_INSTANCE_DIRECT_MERGED_MODULES];
const MODULE_BY_KEY = Object.fromEntries(APP_INSTANCE_MODULES.map((mod) => [mod.key, mod]));

function groupDescriptorsByModule(items: DisplayDescriptor[]) {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

/** 应用实例 ResourcePreview：仅展示 skuResourceModules 勾选的模块 */
export function AppInstanceResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: DeployResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('AppInstance');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const skuResourceModules = form?.getFieldValue('skuResourceModules') as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'AppInstance', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    /** 仅确认预览 UI；prepareSkuSubmit 不传此标记，displayConfig 保持原投影 */
    previewModuleLayout: 'order-fold' as const,
    sku: {
      ...ctx?.sku,
      enabledModules: previewEnabledModules,
    },
  };

  const formValues = form ? (form.getFields() as Record<string, unknown>) : undefined;
  const resolvedSpec = spec ?? (formValues ? plugin.pipeline.fromForm(formValues, previewCtx) : undefined);
  const profile = plugin.pipeline.projectionProfile;
  const descriptorCtx = formValues ? { ...previewCtx, formValues } : previewCtx;
  const flatItems =
    descriptors ??
    (resolvedSpec && isSkuCapableProfile(profile) ? profile.projections.buildDisplayDescriptors(resolvedSpec, descriptorCtx) : []);

  const grouped = groupDescriptorsByModule(flatItems);
  const visibleModuleKeys = ALL_MODULE_KEYS.filter(
    (key) => enabledModuleKeys.includes(key) && !APP_INSTANCE_CONFIG_PREVIEW_EXCLUDED_MODULES.has(key),
  );

  if (!visibleModuleKeys.length) {
    return null;
  }

  return (
    <>
      {visibleModuleKeys.map((key) => {
        const mod = MODULE_BY_KEY[key];
        const items = grouped.get(key) ?? [{ id: `${key}-empty`, group: key, label: '-', value: PREVIEW_EMPTY }];

        return (
          <Card key={key} bordered={false} title={mod?.title ?? key} style={{ marginBottom: 12 }}>
            <DescriptorDescriptions items={items} column={2} />
          </Card>
        );
      })}
    </>
  );
}
